import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { BookOpen, Loader2, FileText, Check, X, Bell, ChevronRight, PenLine } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { InstructionSignModal } from '../../components/InstructionSignModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to calculate deadline urgency
const getDeadlineStatus = (deadline, isConfirmed) => {
  if (isConfirmed) {
    return { color: 'green', label: 'Gelesen', badge: '✅', urgent: false };
  }
  
  if (!deadline) {
    return { color: 'gray', label: 'Ausstehend', badge: '⏳', urgent: false };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);
  
  const diffTime = deadlineDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { color: 'red', label: 'ÜBERFÄLLIG', badge: '🔴', urgent: true, message: 'Bitte sofort lesen!' };
  } else if (diffDays === 0) {
    return { color: 'red', label: 'HEUTE FÄLLIG', badge: '🔴', urgent: true, message: 'Heute noch lesen!' };
  } else if (diffDays === 1) {
    return { color: 'orange', label: 'DRINGEND', badge: '🟠', urgent: true, message: 'Frist: Morgen' };
  } else if (diffDays <= 3) {
    return { color: 'yellow', label: 'Bald fällig', badge: '🟡', urgent: false, message: `Noch ${diffDays} Tage` };
  } else {
    return { color: 'gray', label: 'Ausstehend', badge: '⏳', urgent: false, message: `Noch ${diffDays} Tage` };
  }
};

export const UnterweisungenNew = () => {
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('offen');
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [fullscreenFile, setFullscreenFile] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Unterschrift-Modal
  const [signModal, setSignModal] = useState(null); // { instruction, retroactive }
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchData();
    // Aktuellen User laden für Unterschrift-Name
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser({ name: payload.name || payload.sub || '', id: payload.sub });
      } catch { /* silent */ }
    }
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const instructionsRes = await axios.get(`${API}/api/instructions`, { headers });
      setInstructions(instructionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error('Fehler beim Laden der Unterweisungen');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (instructionId, signatureBase64 = null) => {
    setConfirmingId(instructionId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/instructions/${instructionId}/confirm`,
        signatureBase64 ? { signature_ma: signatureBase64 } : {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Unterweisung als gelesen & unterschrieben bestätigt');
      setInstructions(prev => prev.map(inst =>
        inst.id === instructionId
          ? { ...inst, is_confirmed: true, confirmed_at: new Date().toISOString() }
          : inst
      ));
      setSelectedInstruction(null);
      setSignModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Bestätigen');
    } finally {
      setConfirmingId(null);
    }
  };

  // Mitarbeiter unterschreibt rückwirkend (Instruction ist bereits gelesen)
  const handleRetroactiveSign = async (instructionId, signatureBase64) => {
    const token = localStorage.getItem('token');
    await axios.put(
      `${API}/api/instructions/${instructionId}/mitarbeiter/${currentUser?.id}/sign-ma`,
      { signature_ma: signatureBase64 },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    toast.success('Unterschrift gespeichert');
    fetchData();
  };

  // Beim Klick: Modal sofort mit Basisdaten öffnen, dann Detail (inkl. file_url) nachladen
  const handleInstructionClick = async (instruction) => {
    setSelectedInstruction(instruction); // Sofort öffnen (Modal zeigt Titel, Kategorie)
    setLoadingDetail(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/instructions/${instruction.id}/detail`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Detail-Daten überschreiben Liste (enthält file_url, content, etc.)
      setSelectedInstruction(res.data);
    } catch (error) {
      console.error('Failed to load instruction detail:', error);
      toast.error('Fehler beim Laden der Unterweisung');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Filter instructions based on active tab
  const openInstructions = instructions.filter(i => !i.is_confirmed);
  const readInstructions = instructions.filter(i => i.is_confirmed);
  const displayedInstructions = activeTab === 'offen' ? openInstructions : readInstructions;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-3">
        {/* Simple Header (no bell, it's in DashboardLayout now) */}
        <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Unterweisungen</h1>
        </div>

        {/* Tabs - Sticky */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 sticky top-[68px] sm:top-[72px] bg-white dark:bg-gray-900 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6">
          <button
            onClick={() => setActiveTab('offen')}
            className={`flex-1 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors relative ${
              activeTab === 'offen'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            OFFEN
            {openInstructions.length > 0 && (
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                {openInstructions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('gelesen')}
            className={`flex-1 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors relative ${
              activeTab === 'gelesen'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            GELESEN
          </button>
        </div>

        {/* Content */}
        {displayedInstructions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
              <div className={`p-3 sm:p-4 rounded-full mb-3 sm:mb-4 ${
                activeTab === 'offen' ? 'bg-blue-100' : 'bg-green-100'
              }`}>
                {activeTab === 'offen' ? (
                  <BookOpen className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600" />
                ) : (
                  <Check className="h-10 w-10 sm:h-12 sm:w-12 text-green-600" />
                )}
              </div>
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">
                {activeTab === 'offen' ? 'Keine offenen Unterweisungen' : 'Keine gelesenen Unterweisungen'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center max-w-md px-4">
                {activeTab === 'offen'
                  ? 'Alle Unterweisungen wurden gelesen.'
                  : 'Sie haben noch keine Unterweisungen gelesen.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {displayedInstructions.map(instruction => {
              const status = getDeadlineStatus(instruction.deadline, instruction.is_confirmed);
              
              return (
                <Card
                  key={instruction.id}
                  data-testid={`instruction-item-${instruction.id}`}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    status.urgent && !instruction.is_confirmed
                      ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/30'
                      : ''
                  }`}
                  onClick={() => handleInstructionClick(instruction)}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center justify-between gap-2 sm:gap-3">
                      <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                          instruction.is_confirmed ? 'bg-green-100' : status.urgent ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          <FileText className={`h-4 w-4 sm:h-5 sm:w-5 ${
                            instruction.is_confirmed ? 'text-green-600' : status.urgent ? 'text-red-600' : 'text-blue-600'
                          }`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                            {instruction.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                            <span className="text-xs px-1.5 sm:px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              {instruction.category}
                            </span>
                            
                            {instruction.is_confirmed ? (
                              <span className="text-xs text-green-600">
                                {new Date(instruction.confirmed_at).toLocaleDateString('de-DE')}
                              </span>
                            ) : (
                              <>
                                {instruction.deadline && (
                                  <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded font-medium ${
                                    status.color === 'red' ? 'bg-red-100 text-red-700' :
                                    status.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                    status.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {status.badge} {status.label}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Fullscreen File Viewer */}
        {fullscreenFile && (
          <div className="fixed inset-0 bg-black z-[60] flex flex-col">
            {/* Header */}
            <div className="bg-black text-white p-3 flex items-center justify-between flex-shrink-0">
              <button
                onClick={() => setFullscreenFile(null)}
                className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
                <span className="text-sm">Zurück</span>
              </button>
              <h3 className="text-sm font-medium truncate px-4 flex-1 text-center">
                {fullscreenFile.name}
              </h3>
              <div className="w-16"></div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto bg-black flex items-center justify-center">
              {fullscreenFile.type === 'image' ? (
                <img 
                  src={fullscreenFile.url} 
                  alt={fullscreenFile.name}
                  className="w-full h-full object-contain"
                  style={{ 
                    touchAction: 'pan-x pan-y pinch-zoom',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              ) : fullscreenFile.type === 'pdf' ? (
                <iframe
                  src={fullscreenFile.url}
                  className="w-full h-full bg-white dark:bg-gray-900"
                  title={fullscreenFile.name}
                />
              ) : (
                <div className="text-center text-white p-4">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm mb-4">{fullscreenFile.name}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {selectedInstruction && !fullscreenFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-lg w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-3 sm:p-4 flex items-center justify-between z-10">
                <div className="flex-1 min-w-0 pr-3">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">
                    {selectedInstruction.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">{selectedInstruction.category}</p>
                </div>
                <button
                  onClick={() => setSelectedInstruction(null)}
                  className="p-2 hover:bg-gray-100 dark:bg-gray-800 rounded-full flex-shrink-0"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                {selectedInstruction.description && (
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-4">{selectedInstruction.description}</p>
                )}

                {/* For READ instructions: Show "Bestätigte Dokumente" */}
                {selectedInstruction.is_confirmed ? (
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
                      Bestätigte Dokumente
                    </h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                      {loadingDetail ? (
                        <div className="w-full h-48 flex flex-col items-center justify-center gap-3">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Dokument wird geladen…</p>
                        </div>
                      ) : selectedInstruction.file_type === 'image' ? (
                        <button
                          onClick={() => setFullscreenFile({
                            type: 'image',
                            url: selectedInstruction.file_url,
                            name: selectedInstruction.file_name
                          })}
                          className="w-full group relative"
                        >
                          <img 
                            src={selectedInstruction.file_url} 
                            alt={selectedInstruction.title}
                            className="w-full h-auto rounded"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center rounded">
                            <div className="bg-white dark:bg-gray-900 bg-opacity-90 px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                                Tippen zum Vergrößern
                              </p>
                            </div>
                          </div>
                        </button>
                      ) : selectedInstruction.file_type === 'pdf' ? (
                        <div className="space-y-3">
                          {/* PDF Preview */}
                          <div className="w-full bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                            <iframe
                              src={selectedInstruction.file_url}
                              className="w-full h-[400px] sm:h-[500px]"
                              title={selectedInstruction.file_name}
                            />
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                // Open PDF in new window for multi-page support
                                if (selectedInstruction.file_url.startsWith('data:')) {
                                  // Convert base64 to blob for proper PDF viewing
                                  try {
                                    const arr = selectedInstruction.file_url.split(',');
                                    const mime = arr[0].match(/:(.*?);/)[1];
                                    const bstr = atob(arr[1]);
                                    let n = bstr.length;
                                    const u8arr = new Uint8Array(n);
                                    while(n--) { u8arr[n] = bstr.charCodeAt(n); }
                                    const blob2 = new Blob([u8arr], {type: mime});
                                    const blobUrl2 = URL.createObjectURL(blob2);
                                    const a2 = document.createElement('a');
                                    a2.href = blobUrl2;
                                    a2.target = '_blank';
                                    a2.rel = 'noopener noreferrer';
                                    a2.click();
                                    setTimeout(() => URL.revokeObjectURL(blobUrl2), 10000);
                                  } catch(e) {
                                    const a2 = document.createElement('a');
                                    a2.href = selectedInstruction.file_url;
                                    a2.target = '_blank';
                                    a2.click();
                                  }
                              } else {
                                const a = document.createElement('a');
                                a.href = selectedInstruction.file_url;
                                a.target = '_blank';
                                a.rel = 'noopener noreferrer';
                                a.click();
                              }
                              }}
                              className="flex-1 px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              PDF öffnen
                            </button>
                          </div>
                        </div>
                      ) : !loadingDetail ? (
                        <div className="text-center py-6">
                          <FileText className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Kein Dokument hinterlegt</p>
                        </div>
                      ) : null}
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-center">
                      <Check className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm sm:text-base text-green-800 font-medium">Als gelesen markiert</p>
                      <p className="text-xs sm:text-sm text-green-600 mt-1">
                        {new Date(selectedInstruction.confirmed_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: 'long', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      {/* Rückwirkend unterschreiben */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 gap-1.5 text-xs"
                        onClick={() => setSignModal({ instruction: selectedInstruction, retroactive: true })}
                        data-testid="retroactive-sign-btn"
                      >
                        <PenLine className="h-3.5 w-3.5" />
                        Nachträglich unterschreiben
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* For OPEN instructions: Show file with confirm button */}
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                      {loadingDetail ? (
                        /* PDF/Datei wird vom Server geladen */
                        <div className="w-full h-48 flex flex-col items-center justify-center gap-3">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Dokument wird geladen…</p>
                        </div>
                      ) : selectedInstruction.file_type === 'image' ? (
                        <button
                          onClick={() => setFullscreenFile({
                            type: 'image',
                            url: selectedInstruction.file_url,
                            name: selectedInstruction.file_name
                          })}
                          className="w-full group relative"
                        >
                          <img 
                            src={selectedInstruction.file_url} 
                            alt={selectedInstruction.title}
                            className="w-full h-auto rounded"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center rounded">
                            <div className="bg-white dark:bg-gray-900 bg-opacity-90 px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                                Tippen zum Vergrößern
                              </p>
                            </div>
                          </div>
                        </button>
                      ) : selectedInstruction.file_type === 'pdf' ? (
                        <div className="space-y-3">
                          {/* PDF Preview */}
                          <div className="w-full bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                            <iframe
                              src={selectedInstruction.file_url}
                              className="w-full h-[400px] sm:h-[500px]"
                              title={selectedInstruction.file_name}
                            />
                          </div>
                          
                          {/* Action Button */}
                          <button
                            onClick={() => {
                              if (selectedInstruction.file_url.startsWith('data:')) {
                                try {
                                  const arr = selectedInstruction.file_url.split(',');
                                  const mime = arr[0].match(/:(.*?);/)[1];
                                  const bstr = atob(arr[1]);
                                  let n = bstr.length;
                                  const u8arr = new Uint8Array(n);
                                  while(n--) { u8arr[n] = bstr.charCodeAt(n); }
                                  const blob = new Blob([u8arr], {type: mime});
                                  const blobUrl = URL.createObjectURL(blob);
                                  // Download-Link statt window.open – Chrome blockiert niemals Downloads
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.target = '_blank';
                                  a.rel = 'noopener noreferrer';
                                  a.click();
                                  setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                                } catch(e) {
                                  const a = document.createElement('a');
                                  a.href = selectedInstruction.file_url;
                                  a.target = '_blank';
                                  a.click();
                                }
                              } else {
                                const a = document.createElement('a');
                                a.href = selectedInstruction.file_url;
                                a.target = '_blank';
                                a.rel = 'noopener noreferrer';
                                a.click();
                              }
                            }}
                            className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            PDF öffnen
                          </button>
                        </div>
                      ) : !loadingDetail ? (
                        <div className="text-center py-6">
                          <FileText className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Kein Dokument hinterlegt</p>
                        </div>
                      ) : null}
                    </div>

                    {/* Deadline Info */}
                    {selectedInstruction.deadline && (
                      <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg ${
                        getDeadlineStatus(selectedInstruction.deadline, false).urgent
                          ? 'bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-800'
                          : 'bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-800'
                      }`}>
                        <p className={`text-xs sm:text-sm font-medium ${
                          getDeadlineStatus(selectedInstruction.deadline, false).urgent
                            ? 'text-red-800 dark:text-red-300'
                            : 'text-blue-800 dark:text-blue-300'
                        }`}>
                          {getDeadlineStatus(selectedInstruction.deadline, false).badge} Frist: {' '}
                          {new Date(selectedInstruction.deadline).toLocaleDateString('de-DE')}
                        </p>
                        {getDeadlineStatus(selectedInstruction.deadline, false).message && (
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {getDeadlineStatus(selectedInstruction.deadline, false).message}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Confirm Button → öffnet Unterschrift-Pad */}
                    <Button
                      onClick={() => setSignModal({ instruction: selectedInstruction, retroactive: false })}
                      disabled={confirmingId === selectedInstruction.id}
                      className="w-full bg-green-600 hover:bg-green-700 text-sm sm:text-base py-2 sm:py-3 gap-2"
                      data-testid="gelesen-unterschreiben-btn"
                    >
                      {confirmingId === selectedInstruction.id ? (
                        <><Loader2 className="h-4 w-4 animate-spin" />Wird bestätigt...</>
                      ) : (
                        <><PenLine className="h-4 w-4" />Gelesen & Unterschreiben</>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Unterschrift-Modal */}
      {signModal && (
        <InstructionSignModal
          open={!!signModal}
          onClose={() => setSignModal(null)}
          title={signModal.instruction?.title}
          signerLabel="Mitarbeiter"
          signerName={currentUser?.name}
          confirmLabel={signModal.retroactive ? 'Unterschrift speichern' : 'Gelesen & Unterschreiben'}
          isEmployee={true}
          onSign={async (sig) => {
            if (signModal.retroactive) {
              await handleRetroactiveSign(signModal.instruction.id, sig);
            } else {
              await handleConfirm(signModal.instruction.id, sig);
            }
          }}
        />
      )}
    </DashboardLayout>
  );
};
